import React from 'react';
import classnames from 'classnames';
import {connect} from 'react-redux';
import {distanceInWordsToNow} from 'date-fns';
import {AuthorInfo, Button, SVG} from '@br/br-components';
import Modal from 'react-modal';

import reporter from '../../reporter';

import {contentTypes} from '../../constants/contentTypes';
const {GIF, IMAGE, VIDEO} = contentTypes;

import * as NotificationActions from '../../actions/notificationActions';

import Notification from '../molecules/notification';
import SquareDiv from '../molecules/squareDiv';
import FireCount from '../atoms/fireCount';
import PaperAirplaneSvgIcon from '../atoms/paperAirplaneSvgIcon';
import ShareSvgIcon from '../atoms/shareSvgIcon';
import CommentCount from '../atoms/commentCount';
import BrLogo from '../atoms/brLogo';

import * as TopSelectors from '../../selectors/topLevelSelectors';
import {WarnerMedia} from '../../endpoints';

function PrivacyBanner({ui, checkNotification, onCloseNotification}) {
  return (
    <Notification
      data={{show_privacy_notice: true}}
      name="privacy_notice"
      buttonText={<SVG type="icon" target="close" />}
      ui={ui}
      checkNotification={checkNotification}
      onCloseNotification={onCloseNotification}
    >
      <p>
        By using this site, you agree to the <a href={WarnerMedia.privacy()}>Privacy Policy</a> and{' '}
        <a href="/pages/terms">Terms of Use.</a>
      </p>
    </Notification>
  );
}

function SimpleNav() {
  return (
    <header className="userpostpage__nav">
      <a className="userpostpage__nav-logo" href="/">
        <BrLogo />
      </a>
      <ul className="userpostpage__nav-actions">
        <li>
          <a href="/sign_in" className="login">
            Login
          </a>
        </li>
        <li>
          <a href="/sign_up" className="sign-up">
            Sign Up
          </a>
        </li>
      </ul>
    </header>
  );
}

// Turning off the "alphabetize properties" JS linting rule for our CSS objects
// as the declarations/properties here are listed in SMACSS Property Order.
// https://github.com/sds/scss-lint/blob/169a991/data/property-sort-orders/smacss.txt
/* eslint-disable @br/laws-of-the-game/alphabetize-properties */
const overlayStyles = {
  zIndex: 9999999, // over the PrivacyBanner
  backgroundColor: 'black',
};
const contentStyles = {
  display: 'flex',
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 0,
  borderRadius: 0,
  background: 'transparent',
};
/* eslint-enable @br/laws-of-the-game/alphabetize-properties */

export class UserPostPage extends React.PureComponent {
  static defaultProps = {
    media: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      modalContents: null,
    };
  }

  closeModal = () => {
    this.setState({
      isModalOpen: false,
      modalContents: null,
    });
  };

  showModal = ({src, type}) => {
    this.setState({
      isModalOpen: true,
      modalContents: {
        src,
        type,
      },
    });
  };

  mediaToElement = ({src, type}) => {
    switch (type) {
      case GIF:
      case IMAGE:
        return (
          <a
            href={src}
            key={src}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              event.preventDefault();
              this.showModal({src, type: IMAGE});
            }}
          >
            <img className={`userpostpage__media-${type}`} src={src} />
          </a>
        );
      case VIDEO:
        return (
          <a
            href={src}
            key={src}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              event.preventDefault();
              this.showModal({src, type: VIDEO});
            }}
          >
            <SVG className="userpostpage__videoPlayIcon" type="icon" target="videoPlay" />
            <video autoPlay={false} controls={false} muted={true} preload="true" src={src} />
          </a>
        );
      default:
        reporter.inform(`Unexpected media type: ${type}`);
        return false;
    }
  };

  render() {
    const classes = classnames({
      page: true,
      userPostPage: true,
    });
    const appDotLinkUrl = this.props.appDotLinkUrl;
    const {isModalOpen, modalContents} = this.state;
    return (
      <div className={classes}>
        <PrivacyBanner
          ui={this.props.ui}
          checkNotification={this.props.checkNotification}
          onCloseNotification={this.props.onCloseNotification}
        />
        <SimpleNav />
        <article className="userpostpage__body">
          <header className="userpostpage__meta">
            <AuthorInfo
              info={{...this.props.authorInfo, title: false}}
              photoExists={true}
              link={false}
            />
            <p className="userpostpage__timestamp">
              {distanceInWordsToNow(`${this.props.inserted_at}Z`)} ago
            </p>
          </header>
          <p className="userpostpage__content">{this.props.content.description}</p>
          {this.props.media.length && (
            <SquareDiv className="userpostpage__squarediv">
              <div className="userpostpage__media" data-count={this.props.media.length}>
                {this.props.media.map(this.mediaToElement)}
              </div>
            </SquareDiv>
          )}
          {isModalOpen && modalContents && (
            <Modal
              isOpen={true}
              onRequestClose={this.closeModal}
              style={{overlay: overlayStyles, content: contentStyles}}
            >
              {modalContents.type === IMAGE && (
                <img className="userpostpage__img" src={modalContents.src} />
              )}
              {modalContents.type === VIDEO && (
                <video
                  className="userpostpage__video"
                  autoPlay={true}
                  controls={true}
                  muted={false}
                  preload="auto"
                  src={modalContents.src}
                />
              )}
              <button className="userpostpage__closeButton" onClick={this.closeModal}>
                <SVG type="icon" target="close" />
              </button>
            </Modal>
          )}
          <ul className="userpostpage__reactions">
            <li>
              <a href={appDotLinkUrl}>
                <FireCount count={this.props.numLikes} />
              </a>
            </li>
            <li>
              <a href={appDotLinkUrl}>
                <PaperAirplaneSvgIcon />
              </a>
            </li>
            <li>
              <a href={appDotLinkUrl}>
                <ShareSvgIcon />
              </a>
            </li>
          </ul>
          <CommentCount appDotLinkUrl={appDotLinkUrl} count={this.props.numComments} />
        </article>
        <div className="userpostpage__spacer" />
        <footer className="userpostpage__cta">
          <p>Get the BR app to join the conversation.</p>
          <Button href={appDotLinkUrl}>Install the App!</Button>
        </footer>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const userPost = TopSelectors.userPost(state);
  const ui = TopSelectors.ui(state);
  return {
    ui,
    ...userPost,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    checkNotification: (data) => dispatch(NotificationActions.checkNotification(data)),
    onCloseNotification: (event, name) =>
      dispatch(NotificationActions.closeNotification(event, name)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UserPostPage);
